import type { ContactLink } from './contacts.ts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { ClientTaskRow, Task } from './tasks.ts';
import type { DepartmentChoice } from '../tasks/departmentFilter.ts';

/**
 * The navigation's answers (api/src/routes/navigation.ts). The server decides
 * which modules, menu entries and client sections a reader has; this app draws
 * them and decides nothing. Every route behind them still checks for itself.
 */

export type SectionKey =
  | 'dashboard' | 'legal' | 'financials' | 'property' | 'documents'
  | 'reports' | 'tasks' | 'sw' | 'communications' | 'time';

export interface ToolbarModule {
  key: string;
  label: string;
  path: string;
  count: number | null;
  reserved?: boolean;
}

export interface Toolbar {
  landing: string;
  modules: ToolbarModule[];
  menu: { key: string; label: string; path: string }[];
  lookup: boolean;
  kind_review: boolean;
  /** W1: "Found in the documents" under Legal, exactly where the page's own gate (176) would let the reader in. */
  found_in_documents: boolean;
  client_pages: boolean;
  may_add_client: boolean;
}

export interface MySections {
  client_pages: boolean;
  sections: SectionKey[];
  may_record_visit: boolean;
  tasks_own_only: boolean;
  /** Legal at write: the judge and the evaluator at intake, People on the case (section 13). */
  may_record_people: boolean;
}

/**
 * The toolbar, with the reader's own counts. A minute fresh: the counts are a
 * prompt, not a ledger, and every write that changes one invalidates ['toolbar'].
 */
export function useToolbar() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['toolbar'],
    queryFn: () => apiFetch<Toolbar>('/me/toolbar', getToken),
    ...queryDefaults,
    staleTime: 60_000,
  });
}

export function useMySections() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['me', 'sections'],
    queryFn: () => apiFetch<MySections>('/me/sections', getToken),
    ...queryDefaults,
    staleTime: 5 * 60_000,
  });
}

export interface NextVisit {
  last_visit: string | null;
  interval_days: number | null;
  due_on: string | null;
  days_until: number | null;
  is_overdue: boolean | null;
  visits_this_year: number | null;
  required_this_year: number | null;
  basis: string | null;
}

export interface DueDeadline {
  id: string;
  label: string;
  deadline_type: string;
  due_date: string | null;
  is_statutory: boolean;
  /** Withheld (null) for everyone but the System Administrator and the CPO (A-#86 B1). */
  is_provisional: boolean | null;
  status: string;
  days_until: number | null;
  /** 122: a per-property deadline (the initial site visit) names its property. */
  property_id: string | null;
  property_name: string | null;
}

/** A client's task as its pages list it: 147's Task object plus the list's own fields (contract v3.1). */
export type ClientTask = ClientTaskRow;

export interface ClientSummary {
  sections: SectionKey[];
  identity: {
    date_of_birth: string | null; first_known_on: string | null;
    primary_language: string | null; communication_needs: string | null;
  } | null;
  /** Present only with the SW section. */
  next_visit?: NextVisit | null;
  /** Present only with the Legal section. */
  deadlines?: DueDeadline[];
  /** Present only with the Tasks section. */
  tasks?: ClientTask[];
  /** The names the client goes by: live aliases. Never a corrected name's old value. */
  aliases: { id: string; alias_name: string; alias_type: string }[];
  /** Whether Edit details has anything this reader may change. */
  may_edit_details: boolean;
  /**
   * Whether the Client card asks for this reader's masked identifiers (16 October). The decision only:
   * nothing of a number is in the summary.
   */
  identifiers_visible: boolean;
  /** Whether "Record the visit" is offered on a property's site visit (property at write, 122). */
  may_record_site_visit: boolean;
  /**
   * Lives at, as an address (A-#104): the current residence; null when none is recorded. A facility has a name;
   * a home or a family member's home has none, only its address. away_at: where a temporary stay has them now.
   */
  lives_at: LivesAt | null;
}

export interface LivesAt {
  kind: 'facility' | 'home' | 'household';
  name: string | null;
  street: string | null;
  unit: string | null;
  /** The town line's parts (A-#117): each trimmed, null when blank. livesAtLines() joins them. */
  neighbourhood: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  away_at: string | null;
}

export function useClientSummary(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'summary'],
    queryFn: () => apiFetch<ClientSummary>(`/clients/${clientId}/summary`, getToken),
    ...queryDefaults,
  });
}

export function useClientTasks(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'tasks'],
    queryFn: () => apiFetch<{ own_only: boolean; tasks: ClientTask[] }>(`/clients/${clientId}/tasks?limit=200`, getToken),
    enabled,
    ...queryDefaults,
  });
}

/** A-#211 fix 2: one page of a client's closed tasks (Done, Waived, Cancelled), newest closing first. */
export interface ClosedTasksPage { own_only: boolean; tasks: Task[]; more: boolean }
export const CLOSED_PAGE = 50;

/**
 * The client's closed tasks, a page at a time by offset, fetched only once the Closed tab is opened (enabled). Under
 * the open list's key, so whatever refreshes ['client', id, 'tasks'] after a write refreshes this too.
 * P-23 D: one task department ('' is all), filtered by the server before paging. It is in the key, so each department
 * pages on its own and never shows another's cached pages; in the address only when chosen, a fixed word.
 */
export function useClientClosedTasks(clientId: string, enabled: boolean, department: DepartmentChoice = '') {
  const { getToken } = useSession();
  const only = department ? `&department=${encodeURIComponent(department)}` : '';
  return useInfiniteQuery({
    ...queryDefaults,
    queryKey: ['client', clientId, 'tasks', 'closed', department || 'all'],
    queryFn: ({ pageParam }) =>
      apiFetch<ClosedTasksPage>(`/clients/${clientId}/tasks/closed?limit=${CLOSED_PAGE}&offset=${pageParam}${only}`, getToken),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.more ? pages.length * CLOSED_PAGE : undefined),
    enabled,
  });
}

export interface ClientReport {
  deadline_id: string;
  report_type: string;
  label: string;
  period_year: number | null;
  due_date: string | null;
  days_until: number | null;
  urgency: number | null;
  is_statutory: boolean;
  /** Withheld (null) for everyone but the System Administrator and the CPO (A-#86 B1). */
  is_provisional: boolean | null;
  state: string;
  filed_date: string | null;
  filed_date_unknown: boolean | null;
  approved_date: string | null;
  deadline_status: string;
  /** A filing recorded in Procura (119), live: its id to withdraw it by, who recorded it and when. */
  filing_id: string | null;
  recorded_by: string | null;
  recorded_at: string | null;
}

export function useClientReports(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'reports'],
    queryFn: () => apiFetch<{ reports: ClientReport[]; may_file: boolean }>(`/clients/${clientId}/reports`, getToken),
    enabled,
    ...queryDefaults,
  });
}

export interface ClientCompensation {
  id: string;
  kind: string | null;
  monthly_amount: string | null;
  legal_prepay: string | null;
  accounting_prepay: string | null;
  status: string | null;
  is_active: boolean;
  starts_on: string | null;
  ends_on: string | null;
  last_billed_date: string | null;
  court_approved: boolean | null;
  court_order_amount: string | null;
}

export function useClientCompensation(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'compensation'],
    queryFn: () => apiFetch<{ compensation: ClientCompensation[] }>(`/clients/${clientId}/compensation`, getToken),
    enabled,
    ...queryDefaults,
  });
}

export interface PropertyEvent {
  id: string;
  property_id: string;
  disposition: string;
  effective_on: string | null;
  effective_date_unknown: boolean;
  source: string | null;
  ordered_on: string | null;
  notes: string | null;
  created_at: string;
  withdraws_event_id: string | null;
  withdrawn: boolean;
  property_name: string | null;
}

export function usePropertyTimeline(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'property-timeline'],
    queryFn: () => apiFetch<{ events: PropertyEvent[] }>(`/clients/${clientId}/property-timeline`, getToken),
    enabled,
    ...queryDefaults,
  });
}

export interface LegalContact {
  assignment_id: string;
  role_code: string;
  role_label: string;
  name: string | null;
  credentials: string | null;
  phone: string | null;
  email: string | null;
  do_not_contact: boolean;
  do_not_contact_note: string | null;
}

export interface FacilityContact {
  link_id: string;
  name: string | null;
  job_title: string | null;
  role_label: string | null;
  phone: string | null;
  email: string | null;
  is_primary: boolean;
}

export interface ClientContacts {
  /** The client's own people (not Legal: People on the case), current first, then former. */
  own: ContactLink[];
  /** The people for each of the client's properties, for property readers. */
  property: (ContactLink & { property_id: string; property_street: string | null })[];
  may_add: boolean;
  legal_in_people: boolean;
  facility: { id: string; facility_name: string; street: string | null; city: string | null } | null;
  facility_contacts: FacilityContact[];
}

export function useClientContacts(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'contacts'],
    queryFn: () => apiFetch<ClientContacts>(`/clients/${clientId}/contacts`, getToken),
    ...queryDefaults,
  });
}

export interface CaseContact {
  assignment_id: string;
  role_code: string;
  role_label: string;
  name: string | null;
  credentials: string | null;
  phone: string | null;
  email: string | null;
  do_not_contact: boolean;
}

export interface ClientPeople {
  allied: { department: string; assignment_kind: 'Primary' | 'Covering'; staff_name: string; covers_until: string | null }[];
  /**
   * Present only with the Legal section. The evaluator (before the hearing) and the examiner (after
   * appointment) are separate lines, never one under the other (117).
   */
  court?: {
    judge: string | null;
    /** Where the client's judge is not recorded: the Justice on the latest order that names one, as that order's. */
    judge_from_order: { name: string; signed_date: string | null } | null;
    court_name: string | null;
    evaluator: CaseContact[];
    examiner: CaseContact[];
    others: CaseContact[];
  };
  /** Present only with the Legal section. */
  counsel?: { for_allied: CaseContact[]; for_client: CaseContact[]; other: CaseContact[] };
}

export function useClientPeople(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'people'],
    queryFn: () => apiFetch<ClientPeople>(`/clients/${clientId}/people`, getToken),
    ...queryDefaults,
  });
}
